import React from 'react';
import { Check, ExternalLink, Pencil, Trash, ChevronDown } from 'lucide-react';
import { motion } from 'framer-motion';
import { useDirectTheme } from '../../../hooks/useDirectTheme';
import { Z_LAYERS } from '../../../constants/zLayers';
import type { HorizontalMenuProps } from './types';

interface MenuItemProps {
  icon: React.FC<{ className?: string }>;
  onClick: () => void;
  tooltip: string;
  color?: string;
  hoverColor?: string;
  className?: string;
}

const MenuItem: React.FC<MenuItemProps> = ({
  icon: Icon,
  onClick,
  tooltip,
  color,
  hoverColor,
  className,
}) => {
  const { getDirectColor } = useDirectTheme();

  return (
    <li className="mx-1">
      <motion.a
        className={`p-0 flex items-center justify-center ${className || ''}`}
        title={tooltip}
        onClick={onClick}
        whileHover={{
          scale: 1.25,
          color: hoverColor || getDirectColor('actionIconHover'),
          transition: {
            scale: { type: 'spring', stiffness: 100 },
            color: { duration: 0 }, // Immediate color change
          },
        }}
        whileTap={{ scale: 0.95 }}
        style={{ color: color }}
      >
        <Icon className="h-4 w-4" />
      </motion.a>
    </li>
  );
};

export const HorizontalMenu: React.FC<HorizontalMenuProps> = ({
  onUse,
  onOpenInChatGPT,
  onEdit,
  onDelete,
  onExpand,
  isExpanded,
}) => {
  const { getDirectColor } = useDirectTheme();

  return (
    <ul
      //className="menu menu-horizontal bg-base-200 rounded-box flex flex-row items-center py-1 px-1 shadow-md"
      className="menu menu-horizontal bg-base-200 rounded-box flex flex-row items-center pt-2 pb-[-6] px-0"
      style={{
        zIndex: Z_LAYERS.MENU_BUTTON,
        maxWidth: 'calc(100% - 40px)', // Leave space for the expand button
      }}
      role="menu"
      aria-label="Prompt actions"
    >
      <MenuItem
        icon={Check}
        onClick={onUse}
        tooltip="Copy to Clipboard"
        color={getDirectColor('actionIcon')}
        hoverColor="blue"
      />
      <MenuItem
        icon={ExternalLink}
        onClick={onOpenInChatGPT}
        tooltip="Open in ChatGPT"
        color={getDirectColor('actionIcon')}
        hoverColor="blue"
      />
      <MenuItem
        icon={Pencil}
        onClick={onEdit}
        tooltip="Edit"
        color={getDirectColor('actionIcon')}
        hoverColor="blue"
      />
      <MenuItem
        icon={Trash}
        onClick={onDelete}
        tooltip="Delete"
        //color={getDirectColor('actionIcon')}
        color={getDirectColor('actionIcon')}
        hoverColor="#b91c1c" // Darker red for hover
      />
      <MenuItem
        icon={ChevronDown}
        onClick={onExpand}
        tooltip={isExpanded ? 'Hide prompt' : 'Show prompt'}
        color={getDirectColor('actionIcon')}
        className={`transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
        hoverColor="blue"
      />
    </ul>
  );
};
